import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';
import IntellendLogo from '@/components/IntellendLogo';

interface ConsentProps {
  value?: {
    data_consent_accepted?: boolean;
  };
  onChange?: (value: any) => void;
  disabled?: boolean;
  onNext?: () => void;
  onBack?: () => void;
}

interface ConsentDetails {
  consent_details: {
    id: number;
    type: string;
    version: string;
  };
  content: string;
  bap_name: string;
  accepted: boolean | null;
}

/**
 * Format consent text with proper styling:
 * - Convert lines starting with "-" to bullet points
 * - Bold headings like "You hereby confirm and agree that:"
 * - Add proper paragraph spacing
 */
const formatConsentText = (text: string): JSX.Element[] => {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines
    if (!line) {
      continue;
    }

    // Check if line is a bullet point (starts with "-")
    if (line.startsWith('-')) {
      const bulletText = line.substring(1).trim();
      elements.push(
        <li key={key++} className="ml-4 mb-2 text-sm md:text-base text-gray-700 leading-relaxed">
          {bulletText}
        </li>
      );
    }
    // Check if line is a heading (ends with ":")
    else if (line.endsWith(':')) {
      elements.push(
        <p key={key++} className="font-bold text-sm md:text-base text-gray-900 mb-3 mt-4">
          {line}
        </p>
      );
    }
    // Regular paragraph
    else {
      elements.push(
        <p key={key++} className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
          {line}
        </p>
      );
    }
  }

  return elements;
};

/**
 * CaprionConsent - Consent page component for Caprion journey
 *
 * Mobile-first design with single consent checkbox:
 * - Data Sharing Consent (TNC)
 *
 * Perfios consent is auto-accepted in the backend.
 */
const Consent: React.FC<ConsentProps> = ({
  value = {},
  onChange,
  disabled = false,
  onNext,
  onBack
}) => {
  const [dataConsentAccepted, setDataConsentAccepted] = useState(value.data_consent_accepted || false);
  const [consentDetails, setConsentDetails] = useState<ConsentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch both consent details when component mounts
  useEffect(() => {
    const fetchConsentDetails = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch Data Sharing Consent
        const dataConsentResponse = await axios.post(`${API_BASE_URL}/api/get_consent_details/`, {}, {
          withCredentials: true
        });
        
        console.log('[CaprionConsent] Data sharing consent fetched:', dataConsentResponse.data);
        setConsentDetails(dataConsentResponse.data);
        
        // If consent was previously accepted, set the checkbox
        if (dataConsentResponse.data.accepted === true) {
          setDataConsentAccepted(true);
        }
      } catch (error: any) {
        console.error('[Consent] Failed to fetch consent details:', error);
        setError(error.response?.data?.error || 'Failed to load consent details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConsentDetails();
  }, []);

  // Update parent component when consent changes
  useEffect(() => {
    if (onChange) {
      onChange({
        data_consent_accepted: dataConsentAccepted
      });
    }
  }, [dataConsentAccepted, onChange]);

  const handleAcceptAndContinue = async () => {
    if (!dataConsentAccepted) {
      setError('Please accept the consent to continue');
      return;
    }

    if (!consentDetails) {
      setError('Consent details not loaded');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Submit data sharing consent
      const consentResponse = await axios.post(`${API_BASE_URL}/api/submit_consent/`, {
        enc_id: 'WHITESPACE',
        consent_details: {
          id: consentDetails.consent_details.id,
          type: consentDetails.consent_details.type,
          version: consentDetails.consent_details.version
        },
        accepts_terms_and_privacy: dataConsentAccepted
      }, {
        withCredentials: true
      });

      console.log('[Consent] Data sharing consent submitted:', consentResponse.data);

      // Note: Perfios consent is auto-accepted in backend (get_perfios_consent returns true for Caprion)
      // No need to call set-perfios-consent API

      // Advance to next page (login)
      if (onNext) {
        onNext();
      }
    } catch (error: any) {
      console.error('[Consent] Failed to submit consent:', error);
      setError(error.response?.data?.error || 'Failed to submit consent. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      {/* Header with Logos */}
      <div className="bg-intellend-green px-4 py-4 flex-shrink-0">
        <div className="w-full max-w-md mx-auto flex items-center justify-between">
          {/* PhonePe Logo - Left */}
          <div className="bg-white rounded-lg px-2 py-1 w-24 h-10 flex items-center justify-center">
            <img
              src="/logos/phonepe-logo.png"
              alt="PhonePe"
              className="max-h-8 max-w-full object-contain"
            />
          </div>
          
          {/* Intellend Logo - Center */}
          <div className="bg-white rounded-lg px-2 py-1 w-24 h-10 flex items-center justify-center">
            <IntellendLogo size="sm" />
          </div>

          {/* Caprion Logo - Right */}
          <div className="bg-white rounded-lg px-2 py-1 w-24 h-10 flex items-center justify-center">
            <img
              src="/logos/caprion-logo.png"
              alt="Caprion"
              className="max-h-8 max-w-full object-contain"
            />
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="w-full max-w-md mx-auto space-y-6">
          {/* Title */}
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 text-center">
            Data Sharing Consent
          </h1>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Data Sharing Consent Text Box */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            {isLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
            ) : consentDetails ? (
              <div>
                {formatConsentText(consentDetails.content)}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm">Loading consent details...</p>
              </div>
            )}
          </div>

          {/* Consent Checkbox - At bottom of scrollable content */}
          {consentDetails && (
            <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-300">
              <Checkbox
                id="data-consent-checkbox"
                checked={dataConsentAccepted}
                onCheckedChange={(checked) => setDataConsentAccepted(checked as boolean)}
                disabled={disabled || isLoading}
                className="mt-1"
              />
              <label
                htmlFor="data-consent-checkbox"
                className="text-sm md:text-base text-gray-800 cursor-pointer select-none flex-1"
              >
                I agree to the Data Sharing Consent
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Continue Button at Bottom */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-4 shadow-lg">
        <div className="w-full max-w-md mx-auto">
          <Button
            onClick={handleAcceptAndContinue}
            disabled={!dataConsentAccepted || disabled || isLoading || isSaving || !consentDetails}
            size="lg"
            className={`w-full py-6 text-base md:text-lg font-semibold rounded-lg transition-all ${
              dataConsentAccepted && !isSaving
                ? 'bg-intellend-green hover:bg-intellend-green/90 text-white'
                : 'bg-gray-300 text-gray-700 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Consent;
